import Menu from '@/global/components/MenuV2'
import Skeleton from '@/global/components/Skeleton'
import { cn } from '@/lib/utils'
import { usePlanosContext } from '@/pages/Planos'
import { PlanoFixo } from '@/service/api'
import {
    Box,
    Card,
    CardContent,
    Divider,
    TextField,
    Typography,
} from '@mui/material'
import { grey, orange } from '@mui/material/colors'
import { Button } from 'global/components/Button'
import { memo, useEffect, useState } from 'react'

type Props = {
    planos: PlanoFixo[]
    trocar?: boolean
}

export const PlanosCard = memo<Props>(({ planos, trocar }) => {
    const hasPlanos = planos.length > 0

    const [planosMaisCem, planosMenosCem] = [
        planos.filter(p => p.limiteFuncionarios >= 100),
        planos.filter(p => p.limiteFuncionarios < 100),
    ]
    const [planoSelecionado, setPlano] = useState<PlanoFixo>()

    useEffect(() => {
        if (!planoSelecionado && planosMaisCem.at(0)) setPlano(planosMaisCem[0])
    }, [planosMaisCem])

    return (
        <div
            className={cn(
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
                'gap-6 p-4',
                'overflow-y-auto max-h-full h-full',
                'justify-items-center',
            )}
        >
            {[
                hasPlanos
                    ? planosMenosCem?.map((plano, index) => {
                          return (
                              <CardPlan
                                  key={index}
                                  trocar={trocar}
                                  limiteFuncionarios={plano.limiteFuncionarios}
                                  periodicity={plano.periodicity}
                                  preco={+plano.price}
                              />
                          )
                      })
                    : Array.from({ length: 3 }).map((_, index) => (
                          <Box width="300px" key={index}>
                              <Card
                                  elevation={4}
                                  sx={{
                                      borderRadius: '0.75rem',
                                      height: '430px',
                                  }}
                              >
                                  <Skeleton />
                              </Card>
                          </Box>
                      )),
                !!planoSelecionado && (
                    <Box width="300px" key="plano-selecionado">
                        <Card
                            elevation={4}
                            sx={{
                                borderRadius: '0.75rem',
                                height: '430px',
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Typography variant="h6" textAlign="center">
                                Precisa de um plano com mais funcionários?
                            </Typography>
                            <Divider variant="middle" />
                            <TextField
                                label="Faixa de colaboradores"
                                fullWidth
                                select
                                SelectProps={{
                                    value: planoSelecionado.limiteFuncionarios.toString(),
                                }}
                                onChange={({ target: { value: id } }) => {
                                    setPlano(
                                        planosMaisCem.find(
                                            p =>
                                                p.limiteFuncionarios.toString() ==
                                                id,
                                        )!,
                                    )
                                }}
                            >
                                {planosMaisCem?.map((plano, index) => (
                                    <Menu.Item
                                        key={index}
                                        value={plano.limiteFuncionarios.toString()}
                                    >
                                        até {plano.limiteFuncionarios}&nbsp;
                                        funcionários
                                    </Menu.Item>
                                ))}
                            </TextField>
                            <DividerDescontoPlano
                                periodicity={planoSelecionado.periodicity}
                                isDiferenciado={
                                    planoSelecionado.limiteFuncionarios == 0
                                }
                            />
                            <PrecoPlano preco={+planoSelecionado.price} />
                            <DividerPeriodoPlano
                                periodicity={planoSelecionado.periodicity}
                            />
                            <BotaoContratarPlano
                                limiteFuncionarios={
                                    planoSelecionado.limiteFuncionarios
                                }
                                periodicity={planoSelecionado.periodicity}
                                trocar={trocar}
                            />
                        </Card>
                    </Box>
                ),
            ]}
        </div>
    )
})

function CardPlan({
    trocar,
    limiteFuncionarios,
    periodicity,
    preco,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
    preco: number
}) {
    const { PlanoDiferenciado } = usePlanosContext()

    const name = (() => {
        if (limiteFuncionarios == 0 && PlanoDiferenciado) {
            return `até ${PlanoDiferenciado.limiteFuncionarios} funcionários`
        } else if (limiteFuncionarios != 0) {
            return `até ${limiteFuncionarios} funcionários`
        } else {
            return `Plano especial`
        }
    })()

    return (
        <div className="w-[300px]">
            <Card
                elevation={4}
                sx={{
                    borderRadius: '0.75rem',
                    minHeight: '430px',
                }}
            >
                <CardContent>
                    <Box
                        py={2}
                        borderRadius={2}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        color={grey[700]}
                    >
                        <Typography
                            variant="h6"
                            justifyContent="center"
                            alignItems="center"
                            color="primary"
                        >
                            <strong>{name}</strong>
                        </Typography>
                    </Box>
                    <DividerDescontoPlano
                        periodicity={periodicity}
                        isDiferenciado={limiteFuncionarios == 0}
                    />
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        my={
                            ['yearly', 'quarterly'].includes(periodicity)
                                ? 5
                                : 6.45
                        }
                    >
                        <PrecoPlano preco={preco} />
                    </Box>
                    <DividerPeriodoPlano periodicity={periodicity} />
                    <br />
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        px={2}
                    >
                        <BotaoContratarPlano
                            limiteFuncionarios={limiteFuncionarios}
                            periodicity={periodicity}
                            trocar={trocar}
                        />
                    </Box>
                    <PeriodoRenovacaoPlano periodicity={periodicity} />
                </CardContent>
            </Card>
        </div>
    )
}

function PrecoPlano({ preco }: { preco: number }) {
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="baseline"
            color={grey[700]}
            sx={{
                overflowWrap: 'anywhere',
                flexWrap: 'wrap',
            }}
        >
            <Typography ml={-2} variant="h4" color={grey[500]}>
                R$
            </Typography>
            <Typography variant="h2">
                {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                })
                    .format(preco / 100)
                    .split(',')[0]
                    .replace('R$', '')}
            </Typography>
            <Typography variant="h6" color={grey[500]}>
                ,{(preco / 100).toFixed(2).split('.')[1]}
            </Typography>
        </Box>
    )
}

function DividerPeriodoPlano({ periodicity }: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno = periodicity === 'monthly' ? 'mês' : 'ano'

    return (
        <Divider
            variant="middle"
            sx={{
                width: '-webkit-fill-available',
            }}
        >
            <Typography variant="subtitle1" color={grey[600]}>
                <strong>POR {mesAno.toUpperCase()}</strong>
            </Typography>
        </Divider>
    )
}

function PeriodoRenovacaoPlano({
    periodicity,
}: Pick<PlanoFixo, 'periodicity'>) {
    const mesAno =
        periodicity === 'monthly'
            ? 'mês'
            : periodicity === 'quarterly'
              ? 'trimestre'
              : 'ano'

    return (
        <Box px={2} mt={2}>
            <Typography
                variant="body2"
                color={grey[700]}
                letterSpacing={1.4}
                textAlign="center"
            >
                Este plano é automaticamente renovado a cada {mesAno}
            </Typography>
        </Box>
    )
}

const DividerDescontoPlano = ({
    periodicity,
    isDiferenciado,
}: Pick<PlanoFixo, 'periodicity'> & { isDiferenciado: boolean }) => {
    if (isDiferenciado) return <Divider variant="middle" />
    return (
        <Divider
            variant="middle"
            sx={{
                color: orange[900],
            }}
        >
            {periodicity === 'yearly' && <strong>15% desconto</strong>}
            {periodicity === 'quarterly' && <strong>10% desconto</strong>}
        </Divider>
    )
}

function BotaoContratarPlano({
    limiteFuncionarios,
    periodicity,
    trocar,
}: Pick<PlanoFixo, 'limiteFuncionarios' | 'periodicity'> & {
    trocar?: boolean
}) {
    return (
        <Button
            variant="contained"
            size="large"
            fullWidth
            href={
                trocar
                    ? `/planos/alterar?p=${limiteFuncionarios}-${periodicity}`
                    : `/planos/checkout?p=${limiteFuncionarios}-${periodicity}`
            }
        >
            {trocar ? 'Alterar plano' : 'Contratar plano'}
        </Button>
    )
}
